// The language catalog of the localization settings (spec/dashboard.md section 9.4; research/09 section 3.5): the 32 codes
// Balancy offers with their English and native names, and the two presets. `vn` is the legacy Vietnamese code of the
// Steam preset; `vi` is the BCP-47 one, so `vn` is kept for branches that already have it and never offered anew.
export interface LanguageInfo {
  code: string;
  name: string;
  localizedName: string | null;
  deprecated?: boolean;
}

export const LANGUAGE_CATALOG: LanguageInfo[] = [
  { code: 'en', name: 'English', localizedName: null },
  { code: 'zh-CN', name: 'Chinese (Simplified)', localizedName: '简体中文' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', localizedName: '繁體中文' },
  { code: 'de', name: 'German', localizedName: 'Deutsch' },
  { code: 'fr', name: 'French', localizedName: 'Français' },
  { code: 'it', name: 'Italian', localizedName: 'Italiano' },
  { code: 'es', name: 'Spanish (Spain)', localizedName: 'Español (España)' },
  { code: 'es-419', name: 'Spanish (Latin America)', localizedName: 'Español (Latinoamérica)' },
  { code: 'ru', name: 'Russian', localizedName: 'Русский' },
  { code: 'ko', name: 'Korean', localizedName: '한국어' },
  { code: 'ja', name: 'Japanese', localizedName: '日本語' },
  { code: 'pt', name: 'Portuguese', localizedName: 'Português' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', localizedName: 'Português (Brasil)' },
  { code: 'fi', name: 'Finnish', localizedName: 'Suomi' },
  { code: 'ar', name: 'Arabic', localizedName: 'العربية' },
  { code: 'nl', name: 'Dutch', localizedName: 'Nederlands' },
  { code: 'bg', name: 'Bulgarian', localizedName: 'Български' },
  { code: 'da', name: 'Danish', localizedName: 'Dansk' },
  { code: 'cs', name: 'Czech', localizedName: 'Čeština' },
  { code: 'el', name: 'Greek', localizedName: 'Ελληνικά' },
  { code: 'hu', name: 'Hungarian', localizedName: 'Magyar' },
  { code: 'pl', name: 'Polish', localizedName: 'Polski' },
  { code: 'no', name: 'Norwegian', localizedName: 'Norsk' },
  { code: 'ro', name: 'Romanian', localizedName: 'Română' },
  { code: 'sv', name: 'Swedish', localizedName: 'Svenska' },
  { code: 'th', name: 'Thai', localizedName: 'ไทย' },
  { code: 'tr', name: 'Turkish', localizedName: 'Türkçe' },
  { code: 'vn', name: 'Vietnamese (legacy code)', localizedName: 'Tiếng Việt', deprecated: true },
  { code: 'uk', name: 'Ukrainian', localizedName: 'Українська' },
  { code: 'hi', name: 'Hindi', localizedName: 'हिन्दी' },
  { code: 'vi', name: 'Vietnamese', localizedName: 'Tiếng Việt' },
  { code: 'id', name: 'Indonesian', localizedName: 'Bahasa Indonesia' },
];

export const LANGUAGE_PRESETS: { key: string; label: string; codes: string[] }[] = [
  { key: 'mobile', label: '18 languages (mobile)', codes: ['en', 'es', 'pt', 'de', 'fr', 'zh-TW', 'zh-CN', 'ja', 'it', 'ko', 'ru', 'pl', 'tr', 'ar', 'th', 'id', 'nl', 'hi', 'vi'] },
  { key: 'steam', label: '29 languages (Steam)', codes: ['fr', 'en', 'ru', 'ar', 'bg', 'zh-CN', 'zh-TW', 'cs', 'da', 'nl', 'fi', 'de', 'el', 'hu', 'it', 'ja', 'ko', 'no', 'pl', 'pt', 'pt-BR', 'ro', 'es', 'es-419', 'sv', 'th', 'tr', 'uk', 'vi'] },
];

export function languageInfo(code: string): LanguageInfo {
  return LANGUAGE_CATALOG.find((l) => l.code === code) ?? { code, name: code, localizedName: null };
}

/** `English [en]`, the column header of the strings grid and the import layout's header shape. */
export function languageHeader(code: string, name?: string | null): string {
  return `${name ?? languageInfo(code).name} [${code}]`;
}

/** `HUE_TEST_KEY` → `Hue Test Key`: the placeholder of a new string's default-language value (9.1). */
export function humanizeKey(key: string): string {
  return key
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
